<template>
    <div v-if="!floating || visible"
         ref="panel"
         :class="['company-comments', { 'company-comments--floating': floating, 'company-comments--inset': floating && inset }]"
         :style="floating && !inset && panelWidth ? { width: panelWidth + 'px', right: '-' + (panelWidth + 20) + 'px' } : {}">
        <div v-if="floating" class="company-comments__header">
            <span class="company-comments__title">{{ labels.about }} {{ companyName }}</span>
            <button type="button" class="company-comments__close" @click="$emit('close')">&times;</button>
        </div>

        <div class="company-comments__body">
            <div v-for="comment in comments" :key="comment.id" class="company-comments__item">
                <div v-if="editingId === comment.id" class="company-comments__edit">
                    <textarea v-model="editContent" class="form-control" rows="2"></textarea>
                    <div class="company-comments__actions">
                        <button type="button" class="btn-blue" @click="update(comment)">{{ labels.edit }}</button>
                        <button type="button" class="btn" @click="cancelEdit()">{{ labels.cancel }}</button>
                    </div>
                </div>
                <div v-else>
                    <div class="company-comments__meta">
                        <strong>{{ comment.user }}</strong> — {{ comment.created_at }}
                    </div>
                    <div class="company-comments__content">{{ comment.content }}</div>
                    <div v-if="!currentUserId || comment.user_id === currentUserId" class="company-comments__actions">
                        <button type="button" class="company-comments__action-link" @click="startEdit(comment)">{{ labels.edit }}</button>
                        <button type="button" class="company-comments__action-link company-comments__action-link--danger" @click="remove(comment)">{{ labels.delete }}</button>
                    </div>
                </div>
            </div>

            <div v-if="comments.length === 0" class="company-comments__empty">
                {{ labels.noComment }}
            </div>

            <div class="company-comments__add">
                <textarea v-if="adding" v-model="newContent" class="form-control" rows="2" :placeholder="labels.placeholder" ref="addTextarea"></textarea>
                <div class="company-comments__add-actions">
                    <button v-if="adding" type="button" class="btn-blue" @click="store()" :disabled="!newContent.trim()">{{ labels.add }}</button>
                    <button v-if="adding" type="button" class="company-comments__action-link" @click="adding = false; newContent = ''">{{ labels.cancel }}</button>
                    <button v-else type="button" class="company-comments__action-link" @click="openAdd()">+ {{ labels.add }}</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        props: {
            subscriptionId: {type: [String, Number], required: true},
            storeRoute: {type: String, required: true},
            updateRoute: {type: String, required: true},
            deleteRoute: {type: String, required: true},
            initialComments: {type: Array, default: () => []},
            labels: {type: Object, required: true},
            floating: {type: Boolean, default: false},
            visible: {type: Boolean, default: false},
            currentUserId: {type: [String, Number], default: null},
            companyName: {type: String, default: ''},
        },
        data() {
            return {
                comments: [...this.initialComments],
                newContent: '',
                editingId: null,
                editContent: '',
                adding: false,
                inset: false,
                panelWidth: null,
            };
        },
        mounted() {
            this.syncCount();
        },
        watch: {
            visible(val) {
                if (val && this.floating) {
                    this.$nextTick(this.checkSpace);
                    window.addEventListener('resize', this.checkSpace);
                } else {
                    window.removeEventListener('resize', this.checkSpace);
                }
            },
        },
        beforeDestroy() {
            window.removeEventListener('resize', this.checkSpace);
        },
        methods: {
            checkSpace() {
                const MIN_PANEL_WIDTH = 280;
                const MAX_PANEL_WIDTH = 450;
                const parent = this.$el && this.$el.parentElement;
                if (!parent) {
                    this.inset = true;
                    this.panelWidth = null;
                    return;
                }
                const rect = parent.getBoundingClientRect();
                const spaceRight = window.innerWidth - rect.right - 20;
                if (spaceRight < MIN_PANEL_WIDTH) {
                    this.inset = true;
                    this.panelWidth = null;
                } else {
                    this.inset = false;
                    this.panelWidth = Math.min(spaceRight, MAX_PANEL_WIDTH);
                }
            },
            syncCount() {
                if (this.$root && this.$root.commentCounts) {
                    this.$root.$set(this.$root.commentCounts, this.subscriptionId, this.comments.length);
                }
            },
            openAdd() {
                this.adding = true;
                this.$nextTick(() => {
                    if (this.$refs.addTextarea) {
                        this.$refs.addTextarea.focus();
                    }
                });
            },
            store() {
                if (!this.newContent.trim()) return;

                axios.post(this.storeRoute, {
                    company_subscription_id: this.subscriptionId,
                    content: this.newContent,
                }).then(response => {
                    this.comments.push(response.data.comment);
                    this.newContent = '';
                    this.adding = false;
                    this.syncCount();
                }).catch(error => {
                    console.log(error);
                });
            },
            startEdit(comment) {
                this.editingId = comment.id;
                this.editContent = comment.content;
            },
            cancelEdit() {
                this.editingId = null;
                this.editContent = '';
            },
            update(comment) {
                if (!this.editContent.trim()) return;

                const url = this.updateRoute.replace('__COMMENT__', comment.id);
                axios.put(url, {
                    content: this.editContent,
                }).then(response => {
                    const index = this.comments.findIndex(c => c.id === comment.id);
                    if (index !== -1) {
                        this.$set(this.comments, index, response.data.comment);
                    }
                    this.cancelEdit();
                }).catch(error => {
                    console.log(error);
                });
            },
            remove(comment) {
                const url = this.deleteRoute.replace('__COMMENT__', comment.id);
                axios.delete(url).then(() => {
                    this.comments = this.comments.filter(c => c.id !== comment.id);
                    this.syncCount();
                }).catch(error => {
                    console.log(error);
                });
            },
        },
    };
</script>
